import { useState, useCallback, useMemo } from "react";
import moment from "moment";
import { Cafe, PubTournament } from "../../../../api/types";
import { cafeTournaments, getFranchisePub } from "../../../../api/cafe";
import {
  getYoutubeLiveList,
  PubYoutubeLiveResponse,
} from "../../../../api/youtube-live";

interface UseFranchisePubDataReturn {
  allList: Cafe[];
  loading: boolean;
  error: string | null;
  fetchPubList: () => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useFranchisePubData = (
  latitude: number,
  longitude: number
): UseFranchisePubDataReturn => {
  const [allList, setAllList] = useState<Cafe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 토너먼트 데이터 업데이트 로직 개선
  const updateCafeListWithTournaments = useCallback(
    (
      cafeList: Cafe[],
      tournamentList: PubTournament[],
      lives: PubYoutubeLiveResponse[]
    ): Cafe[] => {
      const today = moment();

      return cafeList.map((cafe) => {
        // 토너먼트 필터링 로직 개선
        const relevantTournaments = tournamentList.filter((tournament) => {
          if (tournament.cafeId !== +cafe.id) return false;

          const startDate = moment(tournament.startDate);
          const daysDiff = startDate.diff(today, "days");

          // 1일 전부터 표시 (기존 -1에서 유지)
          return daysDiff >= -1;
        });

        // 라이브 데이터 연결
        const liveData = lives.find((live) => live.pubId === cafe.id);

        return {
          ...cafe,
          pubTournamentList: relevantTournaments,
          youtubeLiveList: liveData?.youtubeLiveList || [],
        };
      });
    },
    []
  );

  // 데이터 페칭 로직 개선
  const fetchPubList = useCallback(async () => {
    if (!latitude || !longitude) return;
    if (allList.length > 0) return;

    setLoading(true);
    setError(null);

    try {
      // 1. 기본 펍 데이터 가져오기
      const pubResponse = await getFranchisePub(latitude, longitude);
      const cafes = pubResponse.list;

      const franchisePubs = cafes.filter((item) => item.pubType !== "RUNNER");

      if (franchisePubs.length === 0) {
        setAllList([]);
        return;
      }

      // 2. 펍 ID 목록 추출
      const pubIds = franchisePubs.map((pub) => pub.id);

      // 3. 병렬로 토너먼트와 라이브 데이터 가져오기
      const [tournamentList, liveData] = await Promise.all([
        cafeTournaments(franchisePubs),
        getYoutubeLiveList(pubIds),
      ]);

      // 4. 데이터 병합 및 상태 업데이트
      const updatedList = updateCafeListWithTournaments(
        franchisePubs,
        tournamentList,
        liveData
      );

      setAllList(updatedList);
    } catch (err) {
      console.error("펍 데이터 로딩 실패:", err);
      setError("데이터를 불러오는데 실패했습니다.");
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  }, [latitude, longitude, updateCafeListWithTournaments]);

  // 데이터 새로고침
  const refreshData = useCallback(async () => {
    await fetchPubList();
  }, [fetchPubList]);

  // 메모화된 반환값
  const returnValue = useMemo(
    () => ({
      allList,
      loading,
      error,
      fetchPubList,
      refreshData,
    }),
    [allList, loading, error, fetchPubList, refreshData]
  );

  return returnValue;
};
